import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Check, ClipboardCopy, Loader2 } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import type { Project, PipelineOutput } from '@/api/types'
import ExportButton from '@/components/ExportButton'
import { formatTime } from '@/lib/utils'
import { Button, Card } from '@/admin-design/_common'

// Truncate the draft preview at this many characters so the page stays
// readable even for 5-minute teleprompter scripts. Operators always have
// the download buttons for the full body.
const DRAFT_PREVIEW_CHARS = 600

export default function ScriptExport() {
  const params = useParams<{ id: string }>()
  const scriptId = Number(params.id)
  const validId = Number.isFinite(scriptId) && scriptId > 0

  const scriptQ = useQuery({
    queryKey: ['script', scriptId],
    queryFn: () => api.getScript(scriptId),
    enabled: validId,
    retry: (failureCount, err) => {
      // Don't retry not-found / unauthorized — operators clicking a stale
      // link should see the error immediately.
      if (err instanceof ApiError && (err.status === 404 || err.status === 401)) {
        return false
      }
      return failureCount < 2
    },
  })

  if (!validId) {
    return <ErrorState message="잘못된 스크립트 ID입니다." />
  }

  if (scriptQ.isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 size={14} className="animate-spin" /> 스크립트를 불러오는 중...
      </div>
    )
  }

  if (scriptQ.isError) {
    const msg =
      scriptQ.error instanceof ApiError
        ? `${scriptQ.error.status} · ${scriptQ.error.message}`
        : (scriptQ.error as Error)?.message ?? '알 수 없는 오류'
    return <ErrorState message={`스크립트를 불러오지 못했습니다: ${msg}`} />
  }

  // Defensive: `useQuery({ enabled })` may yield undefined data even after
  // isLoading/isError are both false (initial idle on disabled queries).
  if (!scriptQ.data) {
    return <ErrorState message="스크립트 데이터를 사용할 수 없어요." />
  }

  const { project, payload } = scriptQ.data
  const hasPayload = Boolean(payload && (payload.outline || payload.hook || payload.draft))
  const ready = project.current_version_id != null && hasPayload

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={12} /> Dashboard
        </Link>
        <h1 className="text-2xl font-semibold text-foreground">{project.title || `Script #${project.id}`}</h1>
        <p className="text-sm text-muted-foreground">
          {project.channel_profile_name || '채널 미지정'} · 업데이트{' '}
          {formatTime(project.updated_at)}
          {project.topic_title ? ` · 주제: ${project.topic_title}` : ''}
        </p>
      </header>

      {!ready ? (
        <div className="rounded-lg border border-warning-soft bg-warning-soft p-4 text-sm text-warning-soft-foreground">
          이 스크립트는 아직 생성된 버전이 없어요. 에디터에서 본문을 만든 뒤 다시 시도하세요.
        </div>
      ) : (
        <>
          <ExportActions scriptId={project.id} />
          <Preview project={project} payload={payload!} />
        </>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────

function ExportActions({ scriptId }: { scriptId: number }) {
  return (
    <Card className="shadow-sm">
      <Card.Body className="p-5">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-foreground">
          다운로드 / 복사
        </h2>
        <div className="flex flex-col gap-3 sm:flex-row">
          <ExportButton
            scriptId={scriptId}
            format="md"
            label="Export as Markdown (.md)"
            variant="primary"
          />
          <ExportButton
            scriptId={scriptId}
            format="txt"
            label="Export as Plain Text (.txt)"
            variant="secondary"
          />
          <CopyButton scriptId={scriptId} />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Markdown은 YouTube 설명란 / Notion에, Plain Text는 텔레프롬프터에 적합해요.
        </p>
      </Card.Body>
    </Card>
  )
}

// CopyButton fetches the rendered markdown body and writes it to the
// system clipboard. Markdown is the better default for clipboard because
// it preserves section structure when pasted into rich-text targets.
function CopyButton({ scriptId }: { scriptId: number }) {
  type State = 'idle' | 'loading' | 'copied' | 'error'
  const [state, setState] = useState<State>('idle')
  const [errMsg, setErrMsg] = useState<string | null>(null)

  const onClick = async () => {
    setState('loading')
    setErrMsg(null)
    try {
      const text = await api.exportScriptText(scriptId, 'md')
      // navigator.clipboard requires a secure context; old browsers may not
      // expose it, in which case we surface the error inline.
      if (!navigator.clipboard?.writeText) {
        throw new Error('이 브라우저는 클립보드 복사를 지원하지 않아요.')
      }
      await navigator.clipboard.writeText(text)
      setState('copied')
      // Reset back to idle after a beat so the operator can copy again.
      window.setTimeout(() => setState('idle'), 1500)
    } catch (err) {
      setState('error')
      setErrMsg(err instanceof Error ? err.message : '복사 실패')
    }
  }

  const labelByState: Record<State, string> = {
    idle: 'Copy full script',
    loading: '복사 중...',
    copied: '복사 완료',
    error: '다시 시도',
  }
  const Icon = state === 'copied' ? Check : state === 'loading' ? Loader2 : ClipboardCopy

  return (
    <div className="flex flex-col">
      <Button
        type="button"
        variant="secondary"
        size="lg"
        onClick={onClick}
        loading={state === 'loading'}
        className="px-4 py-3"
      >
        {state !== 'loading' && (
          <Icon size={16} aria-hidden="true" />
        )}
        <span>{labelByState[state]}</span>
      </Button>
      {state === 'error' && errMsg && (
        <p className="mt-1 text-xs text-destructive">{errMsg}</p>
      )}
    </div>
  )
}

function Preview({ project, payload }: { project: Project; payload: PipelineOutput }) {
  const draft = payload.draft ?? ''
  const truncated = draft.length > DRAFT_PREVIEW_CHARS
  const draftPreview = truncated ? draft.slice(0, DRAFT_PREVIEW_CHARS) + '...' : draft

  return (
    <section className="space-y-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground">미리보기</h2>

      <PreviewCard title="Hook">
        <p className="whitespace-pre-wrap text-sm text-card-foreground">
          {payload.hook || <em className="text-muted-foreground">(생성된 훅이 없어요)</em>}
        </p>
      </PreviewCard>

      <PreviewCard title="Outline">
        <pre className="whitespace-pre-wrap text-sm text-card-foreground font-sans">
          {payload.outline || <em className="text-muted-foreground">(생성된 아웃라인이 없어요)</em>}
        </pre>
      </PreviewCard>

      <PreviewCard
        title="Draft"
        subtitle={
          truncated
            ? `처음 ${DRAFT_PREVIEW_CHARS}자 미리보기 — 전체 본문은 다운로드하세요`
            : undefined
        }
      >
        <pre className="whitespace-pre-wrap text-sm text-card-foreground font-sans">
          {draftPreview || <em className="text-muted-foreground">(생성된 본문이 없어요)</em>}
        </pre>
      </PreviewCard>

      {payload.tokens_used != null && (
        <p className="text-xs text-muted-foreground">
          토큰 사용량 {payload.tokens_used.toLocaleString()} · 프로젝트 #{project.id}
        </p>
      )}
    </section>
  )
}

function PreviewCard({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <Card>
      <Card.Body className="p-4">
        <header className="mb-2 flex items-baseline justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
          {subtitle && <span className="text-[11px] text-muted-foreground">{subtitle}</span>}
        </header>
        {children}
      </Card.Body>
    </Card>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="space-y-3">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={12} /> Dashboard
      </Link>
      <div className="rounded-lg border border-destructive-soft bg-destructive-soft p-4 text-sm text-destructive-soft-foreground">
        {message}
      </div>
    </div>
  )
}
